import { BaJwt } from '@oiambk/oiam-oauth-types';
import { getAssetPath } from '@stencil/core';
import log from 'loglevel';
import { isOiamErrorPayload, OiamErrorPayload } from '../model/errors';
import { OauthService } from './oauth-service';
import { StoreService } from './store-service';

export namespace NginxErrorLogger {
  let _logurl: URL;

  export function setLogUrl(path?: string): void {
    // Special case fallback component
    // it is loaded from npm, getAssetPath does not work correctly in that case
    // just set it hard coded
    if (path) {
      _logurl = new URL(window.location.origin + path);
      return;
    }
    // Log URL is at a path relative to oiam-oauth-wc, but it has to be on the same host as the eService to avoid CORS errors.
    // getAssetPath has the path to our WC, but we have to ensure that the host of the eService' is used.
    const logpath = getAssetPath('../ct/mt/clienterror/');
    // normally getAssetPath returns a relative path
    // only if origin of current page and the origin of the oiam-oauth-wc differs, a full url is returned
    // change the host in such a cases
    if (logpath.startsWith('http://') || logpath.startsWith('https://')) {
      try {
        const scripturl = new URL(logpath);
        // set host to current page host
        scripturl.host = window.location.host;
        _logurl = scripturl;
      } catch (e) {
        log.warn('oiam-oauth-wc: failed to generate nginx error url', logpath, e);
      }
    } else {
      _logurl = new URL(window.location.origin + logpath);
    }
  }

  /**
   * Log error at nginx asynchronously
   * Use browser event queue, so no side effects to other functionality
   * @param error
   */
  export function logError(error: any): void {
    setTimeout(async () => {
      await doLog(error);
    }, 0);
  }

  export async function doLog(error: any): Promise<void> {
    // no error in error log
    try {
      // error type is not always predictable -> convert it
      let err: OiamErrorPayload;
      if (isOiamErrorPayload(error)) {
        err = error;
      } else {
        err = {
          id: 'OIAM-UNKNOWN',
          message: 'Unknown error',
          details: error,
        };
      }
      // get current session id if any
      let sid = '';
      const user = await OauthService.getUser();
      if (user) {
        const baJwt = new BaJwt(user.access_token);
        sid = baJwt.payload.sid;
      }
      const instance = StoreService.getFallbackInstance().instance || StoreService.getInstance().instance;
      _logurl.searchParams.set('sid', sid);
      _logurl.searchParams.set('errorid', err.id);
      _logurl.searchParams.set('errortext', err.message);
      // log error detail if any
      _logurl.searchParams.set('errordetail', formatDetail(err.details));
      _logurl.searchParams.set('owcstate', StoreService.readState());
      _logurl.searchParams.set('authority', StoreService.getAuthority());
      _logurl.searchParams.set('clientid', StoreService.getClientId());
      _logurl.searchParams.set('instancetype', instance.type || 'fallback'); // WC always sets type. if not, it must be fallback
      _logurl.searchParams.set('version', instance.version || 'unknown'); // old fallbacks don't have version in sessionStorage

      // do fetch at nginx to log
      await fetch(_logurl, { headers: { 'correlation-id': instance.id || instance } }); // old fallbacks don't have id => just log instance
    } catch (e) {
      log.warn('Error writing server-side error-log', _logurl, e);
      return;
    }
  }

  export function formatDetail(detail: any): string {
    let formatted = detail || '';
    switch (typeof formatted) {
      case 'number':
      case 'boolean':
      case 'object': {
        // JSON.stringify does not work correctly on Error classes (one gets just empty {})
        // format errors separately
        if (formatted instanceof Error) {
          formatted = formatted.name + ': ' + formatted.message;
        } else {
          try {
            formatted = JSON.stringify(formatted);
          } catch (e) {
            formatted = 'format error: ' + e;
          }
        }
        break;
      }
      case 'bigint': {
        formatted = String(formatted);
        break;
      }
      case 'string': {
        // nothing
        break;
      }
      default: {
        formatted = 'unknown format';
      }
    }
    return formatted;
  }
}
